<template>
  <BaseDialog
    width="460px"
    :show-text="false"
    text-align="left"
    footer-align="flex-end"
    :title="$t(done ? 'carriers.import.password.SavedTitle' : 'carriers.import.password.Title')"
    :confirm-button-text="done ? (testRead ? $t('carriers.import.password.TestSignIn') : '') : $t('buttons.save')"
    :cancel-button-text="done ? $t('buttons.close') : $t('adminTitles.cancel')"
    :confirm-loading="saving || testing"
    :confirm-disable="saving || testing || (!done && !password)"
    @confirm="done ? test() : save()"
    @close="$emit('close')"
  >
    <div class="password-dialog">
      <template v-if="!done">
        <p class="password-dialog__lead">{{ $t('carriers.import.password.Lead', { account: accountName }) }}</p>
        <!-- Hidden by default: this is typed during screen-shares with the client whose login
             it is. The eye is for checking a long password before saving, on purpose only. -->
        <el-input
          ref="input"
          v-model="password"
          :type="shown ? 'text' : 'password'"
          autocomplete="new-password"
          :placeholder="$t(apiKey ? 'carriers.import.password.NewApiKey' : 'carriers.import.password.New')"
          @keyup.enter.native="password && save()"
        >
          <template #suffix>
            <i
              class="password-dialog__eye"
              :class="shown ? 'el-icon-turn-off' : 'el-icon-view'"
              :title="$t(shown ? 'carriers.import.password.Hide' : 'carriers.import.password.Show')"
              @click="shown = !shown"
            ></i>
          </template>
        </el-input>
        <p class="password-dialog__hint">{{ $t('carriers.import.password.Hint') }}</p>
      </template>
      <template v-else>
        <p class="password-dialog__lead">
          {{ testRead ? $t('carriers.import.password.TestLead') : $t('carriers.import.password.SavedLead') }}
        </p>
        <p v-if="testRead && testRead.dryRun" class="password-dialog__hint">{{ $t('carriers.import.password.TestPreview') }}</p>
      </template>
    </div>
  </BaseDialog>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import { apiError, signInTestRead } from '@/utils/carrierImport';
import { UserPermissions } from '@/utils/permissions';

/**
 * "Update password" on a portal account: its own window and its own endpoint
 * (scrape/account/password), so the account form never carries a password and
 * the stored one is never sent back to be kept. Once saved, offers the lightest
 * read there is to prove the portal takes it.
 */
export default {
  name: 'UpdatePasswordDialog',
  components: { BaseDialog },
  mixins: [UserPermissions],
  props: {
    account: { type: Object, required: true },
    /** An API-key portal (Handle): the secret is a key, not a password. */
    apiKey: { type: Boolean, default: false },
  },
  emits: ['close', 'saved', 'started'],
  data() {
    return {
      password: '',
      shown: false,
      saving: false,
      testing: false,
      done: false,
    };
  },
  computed: {
    ...mapGetters(['carrierImportPortals']),
    accountName() {
      return this.account.label || this.account.username || '';
    },
    /** Starting a read is a policy write (scrape/start checks POLICIES_UPDATE): not offered without it. */
    testRead() {
      if (!this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE])) return null;
      return signInTestRead(this.account, this.carrierImportPortals);
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
  },
  methods: {
    save() {
      if (!this.password) return;
      this.saving = true;
      this.$store.dispatch('carrierImportUpdatePassword', {
        scrapeAccountId: this.account.scrapeAccountId,
        password: this.password,
      })
        .then((item) => {
          // Gone from memory as soon as it is stored.
          this.password = '';
          this.shown = false;
          this.done = true;
          this.$emit('saved', item);
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.saving = false; });
    },
    test() {
      if (!this.testRead) return;
      this.testing = true;
      this.$store.dispatch('carrierImportStart', { scrapeAccountId: this.account.scrapeAccountId, ...this.testRead })
        .then(() => {
          this.$notify.success({ message: this.$t('carriers.import.password.TestStarted') });
          this.$store.dispatch('carrierImportFetchRuns').catch(() => {});
          this.$emit('started');
          this.$emit('close');
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.testing = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.password-dialog {
  text-align: left;
  margin-bottom: 8px;

  &__lead {
    font-size: 14px;
    color: var(--solid-dark-purple);
    margin: -4px 0 14px;
    line-height: 1.45;
  }

  &__hint {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 8px;
    line-height: 1.4;
  }

  &__eye {
    cursor: pointer;
    line-height: 40px;
    margin-right: 4px;
  }
}
</style>
